import { defineStore } from 'pinia'
import { useAuthStore } from './authStore'
import {
  getProjectManagementMilestones,
  getProjectManagementProjects,
  getProjectManagementTasks
} from '../features/project-management/api/projectManagementApi'

const PM_CACHE_TTL_MS = 15 * 60 * 1000
const PM_PROJECT_CACHE_VERSION = 'v2'
const PM_MILESTONE_CACHE_VERSION = 'v2'
const PM_TASK_CACHE_VERSION = 'v2'

const readPmCache = (key) => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.timestamp) return null
    if (Date.now() - parsed.timestamp > PM_CACHE_TTL_MS) return null
    return parsed.data
  } catch (err) {
    console.warn('[projectManagementStore] Failed to read cache', err)
    return null
  }
}

const writePmCache = (key, data) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }))
  } catch (err) {
    console.warn('[projectManagementStore] Failed to write cache', err)
  }
}

export const useProjectManagementStore = defineStore('projectManagementStore', {
  state: () => ({
    projects: [],
    milestones: [],
    tasksByJob: {},
    tasksByMilestone: {},
    isLoading: false,
    error: null,
    scheduleFieldsByJob: {}
  }),
  actions: {
    async fetchProjects({
      limit = 100,
      fields = '_ALL, breadcrumbs, type(), job_object_budget(_ALL)',
      // Accelo job statuses (2025-12): 8 Paused, 9 Complete, 12 Cancelled, 14 Active,
      // 15 Planned, 16 Closing, 17 Upgrade - Test Install, 18 Upgrade - Prod Install,
      // 19 Upgrade QA, 22 Design, 23 Development, 24 UAT, 25 Production Support
      filters = 'status(8,14,15,16,17,18,19,22,23,24,25)'
    } = {}) {
      const authStore = useAuthStore()

      if (!authStore.token) {
        this.error = 'Authorization token missing'
        console.warn('[fetchProjects] Missing token. Cannot proceed.')
        return
      }

      this.isLoading = true
      this.error = null

      const twoYearsAgo = Math.floor(new Date().setFullYear(new Date().getFullYear() - 2) / 1000)
      const dateFilter = `date_created_after(${twoYearsAgo})`
      const fullFilters = filters ? `${filters},${dateFilter}` : dateFilter

      console.log('[fetchProjects] Starting request to:', '/project-management/projects')
      console.log('[fetchProjects] Params:', { limit, fields, filters: fullFilters })

      const cacheKey = `pmProjectsCache:${PM_PROJECT_CACHE_VERSION}:${limit}:${fields}:${fullFilters}`
      const cached = readPmCache(cacheKey)
      let usedCache = false
      if (cached) {
        this.projects = cached
        usedCache = true
      }

      try {
        if (!usedCache) {
          const response = await getProjectManagementProjects({ limit, fields, filters: fullFilters })

          console.log('[fetchProjects] Response:', response.data)
          this.projects = response.data.filter(
            p => p.companyName !== 'PerByte - Internal' && p.companyName !== 'FauxByte, Inc.'
          )
          writePmCache(cacheKey, this.projects)
        }

        // Once projects are loaded, fetch their milestones
        const jobIds = this.projects.map(p => p.id).join(',')
        if (jobIds) {
          await this.fetchMilestones(jobIds)
          const milestoneIds = this.milestones.map(m => m.id).join(',')
          await this.fetchTasks(jobIds, milestoneIds)
        }

      } catch (err) {
        this.error = err.message || 'Failed to load projects'
        this.projects = []
        console.error('[fetchProjects] Error:', err)
      } finally {
        this.isLoading = false
        console.log('[fetchProjects] Request complete.')
      }
    },

    async fetchMilestones(jobIds) {
      const authStore = useAuthStore()

      if (!authStore.token) {
        console.warn('[fetchMilestones] Missing token. Cannot proceed.')
        return
      }

      console.log('[fetchMilestones] Requesting milestones for jobs:', jobIds)

      const cacheKey = `pmMilestonesCache:${PM_MILESTONE_CACHE_VERSION}:${jobIds}`
      const cached = readPmCache(cacheKey)
      if (cached) {
        this.milestones = cached
        return
      }

      try {
        const response = await getProjectManagementMilestones({
          limit: 100,
          fields: '_ALL,milestone_object_schedule(_ALL),milestone_object_budget(_ALL)',
          filters: `job(${jobIds})`
        })

        console.log('[fetchMilestones] Response:', response.data)
        this.milestones = response.data
        writePmCache(cacheKey, this.milestones)

      } catch (err) {
        console.error('[fetchMilestones] Error fetching milestones:', err)
        this.error = err.message || 'Failed to load milestones'
        this.milestones = []
      }
    },

    async fetchTasks(jobIds, milestoneIds) {
      const authStore = useAuthStore()

      if (!authStore.token) {
        console.warn('[fetchTasks] Missing token. Cannot proceed.')
        return
      }

      const jobCacheKey = jobIds ? `pmTasksJobCache:${PM_TASK_CACHE_VERSION}:${jobIds}` : null
      const milestoneCacheKey = milestoneIds ? `pmTasksMilestoneCache:${PM_TASK_CACHE_VERSION}:${milestoneIds}` : null
      const cachedJobs = jobCacheKey ? readPmCache(jobCacheKey) : null
      const cachedMilestones = milestoneCacheKey ? readPmCache(milestoneCacheKey) : null

      this.tasksByJob = cachedJobs || {}
      this.tasksByMilestone = cachedMilestones || {}

      try {
        // Fetch tasks for jobs
        if (jobIds && !cachedJobs) {
          const jobFilter = `against_id(${jobIds}),against_type(job)`
          const jobResponse = await getProjectManagementTasks({
            limit: 100,
            fields: '_ALL,assignee()',
            filters: jobFilter
          })

          for (const task of jobResponse.data) {
            if (!this.tasksByJob[task.againstId]) {
              this.tasksByJob[task.againstId] = []
            }
            this.tasksByJob[task.againstId].push(task)
          }
          writePmCache(jobCacheKey, this.tasksByJob)
        }

        // Fetch tasks for milestones
        if (milestoneIds && !cachedMilestones) {
          const milestoneFilter = `against_id(${milestoneIds}),against_type(milestone)`
          const milestoneResponse = await getProjectManagementTasks({
            limit: 100,
            fields: '_ALL,assignee()',
            filters: milestoneFilter
          })

          for (const task of milestoneResponse.data) {
            if (!this.tasksByMilestone[task.againstId]) {
              this.tasksByMilestone[task.againstId] = []
            }
            this.tasksByMilestone[task.againstId].push(task)
          }
          writePmCache(milestoneCacheKey, this.tasksByMilestone)
        }
      } catch (err) {
        console.error('[fetchTasks] Error fetching tasks:', err)
        this.error = err.message || 'Failed to load tasks'
      }
    },
    setScheduleFieldsForJob(jobId, startKey, endKey) {
      const current = this.scheduleFieldsByJob[jobId] || {
        startKey: 'date_planned_start',
        endKey: 'date_planned_due'
      }
      this.scheduleFieldsByJob[jobId] = {
        startKey: startKey !== undefined ? startKey : current.startKey,
        endKey: endKey !== undefined ? endKey : current.endKey
      }
    },
    clearProjects() {
      console.log('[clearProjects] Clearing stored projects and milestones.')
      this.projects = []
      this.milestones = []
      this.tasksByJob = {}
      this.tasksByMilestone = {}
    }
  },
  getters: {
    activeProjects: (state) => {
      return state.projects.filter(project => project.standing === 'active')
    },
    projectMilestones: (state) => (jobId) => {
      return state.milestones.filter(m => m.job === jobId)
    },
    projectTasks: (state) => (jobId) => {
      return state.tasksByJob[jobId] || []
    },
    milestoneTasks: (state) => (milestoneId) => {
      return state.tasksByMilestone[milestoneId] || []
    },
    scheduleFieldsForJob: (state) => (jobId) =>
      state.scheduleFieldsByJob[jobId] ?? { startKey: 'date_planned_start', endKey: 'date_planned_due' },
  }
})
